/**
 * Shared Apollo mutation registration for the extraction line-item
 * editing flow (perf #11 F1 — SE-4333 item N).
 *
 * Previously every `ExtractionDataSectionContainer` mounted four
 * independent `useMutation` hooks (`useAddLineItem`,
 * `useEditLineItem`, `useDeleteLineItem`,
 * `useResolveCategoryConflicts`). On a six-category contract that
 * added 24 Apollo hook mounts per drawer open — four per section ×
 * six expanded sections — each with its own reconciler bookkeeping
 * and cache subscription.
 *
 * This hook mounts the four mutations once at a shared parent
 * (`ContractExtractionViewContainer`) and exposes the aggregated
 * callbacks + in-flight flags so downstream sections can consume
 * them as props instead of re-mounting. Net: 4 mutation hooks total
 * across the drawer, regardless of how many categories are expanded.
 * @module features/contracts/hooks/useContractLineItemMutations
 */

import { useMemo } from "react";

import { useAddLineItem } from "./useAddLineItem";
import { useDeleteLineItem } from "./useDeleteLineItem";
import { useEditLineItem } from "./useEditLineItem";
import { useResolveCategoryConflicts } from "./useResolveCategoryConflicts";

/**
 * Aggregated shape of the four line-item mutations plus their
 * in-flight flags. Section consumers treat this as an opaque bundle
 * — they call the functions and react to the flags but never reach
 * through to the underlying Apollo hooks.
 */
export interface ContractLineItemMutations {
  readonly addLineItem: ReturnType<typeof useAddLineItem>["addLineItem"];
  readonly editLineItem: ReturnType<typeof useEditLineItem>["editLineItem"];
  readonly deleteLineItem: ReturnType<
    typeof useDeleteLineItem
  >["deleteLineItem"];
  readonly resolveCategoryConflicts: ReturnType<
    typeof useResolveCategoryConflicts
  >["resolveCategoryConflicts"];
  readonly isAdding: boolean;
  readonly isEditingMutation: boolean;
  readonly isDeletingMutation: boolean;
  readonly isResolving: boolean;
}

/**
 * Mounts the four line-item mutations once and memoises the resulting
 * bundle so consumers can pass it down as a prop without triggering
 * unnecessary re-renders on each parent render. The memo key is the
 * eight underlying values — as long as none of them change, the same
 * bundle reference is returned.
 * @returns The aggregated mutation bundle
 */
export function useContractLineItemMutations(): ContractLineItemMutations {
  const { addLineItem, isAdding } = useAddLineItem();
  const { editLineItem, isEditing: isEditingMutation } = useEditLineItem();
  const { deleteLineItem, isDeleting: isDeletingMutation } =
    useDeleteLineItem();
  const { resolveCategoryConflicts, isResolving } =
    useResolveCategoryConflicts();

  return useMemo<ContractLineItemMutations>(
    () => ({
      addLineItem,
      editLineItem,
      deleteLineItem,
      resolveCategoryConflicts,
      isAdding,
      isEditingMutation,
      isDeletingMutation,
      isResolving,
    }),
    [
      addLineItem,
      editLineItem,
      deleteLineItem,
      resolveCategoryConflicts,
      isAdding,
      isEditingMutation,
      isDeletingMutation,
      isResolving,
    ]
  );
}
